import React, { useEffect, useState } from "react";
import { Tooltip } from "antd";
import { Loader } from "lucide-react";
import { AN_COLORS } from "../../../design-system/tokens";
import { MonoCell, StatusPill, fmtDate } from "../../../design-system/tableUi";
import { DOMAIN_ID, ORG_ID } from "../../../constants";
import { getWalletUserList } from "../../../service/didService";
import { SetupChip, muted } from "./passwordUi";

// Read-only cells for a password account (a row of /passwords/listaccounts),
// shared by the tables that show an account's credential and rotation without
// editing them (Endpoint Users, the endpoint's Endpoint User tab). `pw` is the
// account, or null/undefined when no profile has ever named it.

const CRED_LABEL = { key: "Key", password: "Password" };

export const accountKey = (endpointId, account) => `${endpointId}/${account}`;

// Key or Password — what sessions open the account with — and why.
export function CredentialSummary({ pw }) {
  if (!pw || !pw.policyId) {
    return (
      <Tooltip title="Add it to a rotation profile to use a password">
        <span style={{ fontSize: 12.5, color: AN_COLORS.ink2 }}>Key</span>
      </Tooltip>
    );
  }
  const sub = pw.credentialOverride ? `(set: ${CRED_LABEL[pw.credentialOverride] || pw.credentialOverride})` : "(follows profile)";
  return (
    <span style={{ fontSize: 12.5, whiteSpace: "nowrap" }}>
      <span style={{ fontWeight: 500, color: AN_COLORS.ink }}>{CRED_LABEL[pw.inUse] || "Key"}</span>{" "}
      <span style={{ ...muted }}>{sub}</span>
    </span>
  );
}

export function RotationPill({ pw }) {
  if (pw.status === "managed") return <StatusPill tone="success" label="Rotated" />;
  if (pw.status === "pending") return <StatusPill tone="neutral" label="Waiting for first rotation" />;
  if (pw.status === "no_password")
    return (
      <Tooltip title="The account has no password; Authnull doesn't give it one">
        <span>
          <StatusPill tone="neutral" label="No password, left locked" />
        </span>
      </Tooltip>
    );
  if (pw.status === "failed")
    return (
      <Tooltip title={pw.detail || "The last rotation failed."}>
        <span>
          <StatusPill tone="danger" label={pw.failures > 1 ? `Failed ×${pw.failures}` : "Failed"} />
        </span>
      </Tooltip>
    );
  return <StatusPill status={pw.status} />;
}

// Profile name, status (or the Re-run setup chip) and when it's next due.
// onOpenSetup(endpointId) makes the chip a link to the endpoint's Gateway Access tab.
export function RotationSummary({ pw, onOpenSetup }) {
  if (!pw) return <span style={{ color: AN_COLORS.muted }}>–</span>;
  if (!pw.policyId)
    return (
      <Tooltip title="No profile rotates this account any more. Its password stays in the vault.">
        <span style={muted}>No profile</span>
      </Tooltip>
    );
  return (
    <div style={{ display: "grid", gap: 4, justifyItems: "start", fontSize: 12.5 }}>
      <span style={{ color: AN_COLORS.ink2, fontWeight: 500 }}>{pw.policyName || "Rotation profile"}</span>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
        {pw.needsSetup ? (
          <SetupChip onClick={() => onOpenSetup && onOpenSetup(pw.endpointId)} />
        ) : (
          <RotationPill pw={pw} />
        )}
        {pw.jobRunning && (
          <span style={{ ...muted, display: "inline-flex", alignItems: "center", gap: 4 }}>
            <Loader size={12} /> Rotating…
          </span>
        )}
      </span>
      {pw.rerotateAt ? (
        <span style={{ ...muted, color: AN_COLORS.warn }}>Re-rotates at {fmtDate(pw.rerotateAt)} (after a reveal)</span>
      ) : (
        pw.nextDueAt && pw.status !== "no_password" && !pw.needsSetup && <span style={muted}>next {fmtDate(pw.nextDueAt)}</span>
      )}
    </div>
  );
}

export const VersionCell = ({ pw }) => <MonoCell>{pw && pw.version > 0 ? `v${pw.version}` : "—"}</MonoCell>;

// users: { [userId]: email } from useUserEmails().
export function RevealedCell({ pw, users }) {
  if (!pw || !pw.revealedAt) return <span style={muted}>{pw ? "Never" : "—"}</span>;
  return (
    <div style={{ display: "grid", gap: 2 }}>
      <span style={{ fontSize: 12.5, color: AN_COLORS.ink2, whiteSpace: "nowrap" }}>{fmtDate(pw.revealedAt)}</span>
      {pw.revealedBy ? <span style={muted}>by {users[pw.revealedBy] || `user #${pw.revealedBy}`}</span> : null}
    </div>
  );
}

// Who revealed: user ids → emails, best effort. Skipped when !enabled.
export function useUserEmails(enabled = true) {
  const [users, setUsers] = useState({});
  useEffect(() => {
    if (!enabled) return;
    getWalletUserList({
      payload: { domainId: DOMAIN_ID(), orgId: ORG_ID(), pageNumber: 1, pageSize: 1000, filter: "" },
      onSuccess: (res) => {
        const m = {};
        (res?.users || []).forEach((u) => {
          if (u.userId) m[u.userId] = u.emailAddress || u.username || "";
        });
        setUsers(m);
      },
      onFailure: () => {},
    });
  }, [enabled]);
  return users;
}
